import { Order } from '@diamond/shared/types';

import { resolveOrderDetailValue } from './resolve-order-detail-value';

type OrderDetailBillingFields = Pick<
  Order,
  | 'checkout_total'
  | 'latest_original_total'
  | 'latest_saved_total'
  | 'latest_total'
  | 'saved_total'
  | 'total'
>;

export type OrderDetailBillingSource = OrderDetailBillingFields & {
  payment_method?: Order['payment_method'];
  payment?: Pick<NonNullable<Order['payment']>, 'total' | 'total_diff'> | null;
};

export type OrderDetailBillingDisplay = {
  subTotal: number;
  savedTotal: number;
  finalSubtotal: number;
  originalTotal?: number;
  showOriginalTotal: boolean;
};

function shouldShowCurrentOriginalTotal(order: OrderDetailBillingSource) {
  return (
    order.payment_method === 'bank_transfer' &&
    (order.payment?.total_diff ?? 0) !== 0 &&
    order.payment?.total !== undefined
  );
}

function getCurrentOrderDetailBillingDisplay(
  order: OrderDetailBillingSource
): OrderDetailBillingDisplay {
  return {
    subTotal: order.total + order.saved_total,
    savedTotal: order.saved_total,
    finalSubtotal: order.total,
    originalTotal: order.payment?.total,
    showOriginalTotal: shouldShowCurrentOriginalTotal(order),
  };
}

function getPoDetailsBillingDisplay(
  order: OrderDetailBillingSource,
  currentDisplay: OrderDetailBillingDisplay
): OrderDetailBillingDisplay {
  return {
    subTotal: resolveOrderDetailValue(
      order.latest_original_total,
      currentDisplay.subTotal
    ),
    savedTotal: resolveOrderDetailValue(
      order.latest_saved_total,
      currentDisplay.savedTotal
    ),
    finalSubtotal: resolveOrderDetailValue(
      order.latest_total,
      currentDisplay.finalSubtotal
    ),
    originalTotal: resolveOrderDetailValue(
      order.checkout_total,
      currentDisplay.originalTotal
    ),
    showOriginalTotal: (order.payment?.total_diff ?? 0) !== 0,
  };
}

export function getOrderDetailBillingDisplay(
  order: OrderDetailBillingSource,
  poDetailsDataEnabled = false
): OrderDetailBillingDisplay {
  const currentDisplay = getCurrentOrderDetailBillingDisplay(order);

  if (!poDetailsDataEnabled) {
    return currentDisplay;
  }

  return getPoDetailsBillingDisplay(order, currentDisplay);
}
